import { FC } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useQuery } from '@apollo/client';
import { AI_PIPELINE_TRAIL } from './gql';
import { formatDateUS, formatTime } from '../../utils/commonFn';

// ---------------------------------------------------------------------------
// Props contract
// ---------------------------------------------------------------------------

export interface ActionTrailModalProps {
  isOpen: boolean;
  pipelineEntryId: string | null;
  candidateName: string | null | undefined;
  candidateTitle: string | null | undefined;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Status label map
// Converts SCREAMING_SNAKE_CASE enum keys → human-readable labels.
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  SEND_OUTREACH: 'Send outreach',
  IN_QUEUE: 'In queue',
  OUTREACH_SENT: 'Outreach sent',
  AWAITING_RESPONSE: 'Awaiting response',
  SCHEDULED: 'Scheduled',
  INTERVIEWED: 'Interviewed',
  COMPLETED: 'Completed',
  DECLINED_BY_ADMIN: 'Declined by admin',
  NEEDS_HUMAN_OUTREACH: 'Needs human outreach',
  ASSIGNED_TO_RECRUITER: 'Assigned to recruiter',
};

function labelForStatus(status: string | null | undefined): string {
  if (!status) return '';
  return STATUS_LABELS[status] ?? status;
}

// ---------------------------------------------------------------------------
// Trail entry types (shape returned by AI_PIPELINE_TRAIL)
// ---------------------------------------------------------------------------

interface TrailActorUser {
  id: string;
  fullName: string | null | undefined;
}

interface TrailEntry {
  id: string;
  pipelineEntryId: string | null | undefined;
  fromStatus: string | null | undefined;
  toStatus: string;
  reason: string | null | undefined;
  detail: string | null | undefined;
  actorUserId: string | null | undefined;
  actorUser: TrailActorUser | null | undefined;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildActionLabel(entry: TrailEntry): string {
  const to = labelForStatus(entry.toStatus);
  if (!entry.fromStatus) {
    return to;
  }
  return `${labelForStatus(entry.fromStatus)} → ${to}`;
}

function buildActorLabel(entry: TrailEntry): string {
  return entry.actorUser?.fullName?.trim() || 'System';
}

function buildTimestampLabel(createdAt: string): string {
  return `${formatDateUS(createdAt)} ${formatTime(createdAt)}`;
}

// ---------------------------------------------------------------------------
// Timeline dot colours — match status tone from SCSS tokens
// ---------------------------------------------------------------------------

const DOT_COLORS: Record<string, string> = {
  NEW: '#6c757d',               // neutral grey
  SEND_OUTREACH: '#eaa827',     // gold / warn
  IN_QUEUE: '#6c757d',
  OUTREACH_SENT: '#2e95b4',
  AWAITING_RESPONSE: '#2e95b4',
  SCHEDULED: '#2e95b4',
  INTERVIEWED: '#28a745',
  COMPLETED: '#28a745',
  DECLINED_BY_ADMIN: '#dc3545', // danger
  NEEDS_HUMAN_OUTREACH: '#2e95b4', // info
  ASSIGNED_TO_RECRUITER: '#28a745', // active / success
};

function dotColorForStatus(status: string | null | undefined): string {
  if (!status) return '#6c757d';
  return DOT_COLORS[status] ?? '#6c757d';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const TrailEntryRow: FC<{ entry: TrailEntry; isLast: boolean }> = ({
  entry,
  isLast,
}) => {
  const dotColor = dotColorForStatus(entry.toStatus);
  const actor = buildActorLabel(entry);
  const timestamp = buildTimestampLabel(entry.createdAt);

  return (
    <li
      style={{
        display: 'flex',
        gap: '12px',
        position: 'relative',
        paddingBottom: isLast ? 0 : '20px',
      }}
    >
      {/* Connector column */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: '16px',
        }}
      >
        {/* Dot */}
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
            marginTop: '3px',
          }}
        />
        {/* Connector line below dot (hidden on last item) */}
        {!isLast && (
          <div
            style={{
              flex: 1,
              width: '2px',
              background: '#eeeeee',
              marginTop: '4px',
            }}
          />
        )}
      </div>

      {/* Content column */}
      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : '4px' }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#33475b',
            marginBottom: '2px',
          }}
        >
          {buildActionLabel(entry)}
        </div>
        {entry.detail && (
          <div
            style={{
              fontSize: '0.8rem',
              color: '#33475b',
              marginBottom: '2px',
              wordBreak: 'break-word',
            }}
          >
            {entry.detail}
          </div>
        )}
        <div
          style={{
            fontSize: '0.75rem',
            color: '#738699',
          }}
        >
          {actor} &middot; {timestamp}
        </div>
      </div>
    </li>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ActionTrailModal: FC<ActionTrailModalProps> = ({
  isOpen,
  pipelineEntryId,
  candidateName,
  candidateTitle,
  onClose,
}) => {
  const { data, loading, error } = useQuery<{ aiPipelineTrail: TrailEntry[] }>(
    AI_PIPELINE_TRAIL,
    {
      variables: { pipelineEntryId },
      skip: !isOpen || !pipelineEntryId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const trail: TrailEntry[] = data?.aiPipelineTrail ?? [];

  // Header subtitle: "Name — Title" if both present, else just whichever exists
  const headerSubtitle = [candidateName, candidateTitle]
    .filter(Boolean)
    .join(' — ');

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md" style={{ maxWidth: '480px' }}>
      <ModalHeader toggle={onClose} style={{ borderBottom: '1px solid #eeeeee' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#33475b' }}>
          {headerSubtitle || 'Candidate'}
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: '20px 24px' }}>
        <h6
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#738699',
            marginBottom: '16px',
          }}
        >
          Action trail
        </h6>

        {/* Loading state */}
        {loading && (
          <p className="text-muted text-center py-4" style={{ fontSize: '0.875rem' }}>
            Loading…
          </p>
        )}

        {/* Error state */}
        {!loading && error && (
          <p className="text-danger text-center py-4" style={{ fontSize: '0.875rem' }}>
            Could not load the action trail. Please try again.
          </p>
        )}

        {/* Empty state */}
        {!loading && !error && trail.length === 0 && (
          <p className="text-muted text-center py-4" style={{ fontSize: '0.875rem' }}>
            No actions recorded yet.
          </p>
        )}

        {/* Timeline */}
        {!loading && !error && trail.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
            }}
          >
            {trail.map((entry, index) => (
              <TrailEntryRow
                key={entry.id}
                entry={entry}
                isLast={index === trail.length - 1}
              />
            ))}
          </ul>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ActionTrailModal;
